import { useState, useRef, useEffect, useMemo } from "react";
import { parseEther } from "viem";
import { motion, AnimatePresence } from "framer-motion";
import ScrollReveal from "@/components/ScrollReveal";
import TickingNumber from "@/components/TickingNumber";
import { toast } from "sonner";
import {
  Shield,
  TrendingUp,
  ArrowDownLeft,
  ArrowUpRight,
  RefreshCw,
  CheckCircle,
  Info,
  Gift,
  Lock,
  Clock,
  BarChart2,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  AlertCircle,
  Repeat,
  FileText,
  Flame,
  CircleCheckBig,
} from "lucide-react";
import { formatUSDT } from "@/lib/mockData";
import {
  formatEtherWithDecimals,
  formatNumberInput,
  formatKMBTruncate,
} from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAccount } from "wagmi";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useGetCustodyConfig } from "@/hooks/api/useGetCustodyConfig";
import { useGetCustodyOrders } from "@/hooks/api/useGetCustodyOrders";
import { useGetCustodyExitRecords } from "@/hooks/api/useGetCustodyExitRecords";
import { useGetUserCustodyInfo } from "@/hooks/api/useGetUserCustodyInfo";
import { useGetCustodyDailyPayoutRecords } from "@/hooks/api/useGetCustodyDailyPayoutRecords";
import { useGetCustodyStaticClaimLines } from "@/hooks/api/useGetCustodyStaticClaimLines";
import { useSignCustodyAgreement } from "@/hooks/api/useSignCustodyAgreement";
import { useCustodyDeposit } from "@/hooks/api/useCustodyDeposit";
import { useCustodyReinvest } from "@/hooks/api/useCustodyReinvest";
import { useCustodyExit } from "@/hooks/api/useCustodyExit";
import { useCustodyClaim } from "@/hooks/api/useCustodyClaim";
import { useBalanceV2 } from "@/hooks/api/useBalanceV2";
import PullToRefresh from "@/components/PullToRefresh";
import { useTranslation } from "react-i18next";
import type { CustodyExitRecordItem, CustodyOrderItem } from "@/api/types";
import {
  CustodyDepositResultCode,
  CustodyExitResultCode,
  CustodyReinvestResultCode,
} from "@/api/types";
import { CCC_AIF_ADDRESS, USDT_ADDRESS } from "@/config/env";
import { getTokenName } from "@/config/common";
import {
  CUSTODY_TSK_TOKEN_LAUNCHED,
  custodyTskLockedCardStyle,
  formatCustodyTskUsdValue,
} from "@/lib/custodyTskUi";
import {
  calcCustodyMonthProgress,
  calcUtcInclusiveCalendarDays,
} from "@/lib/custodyMonthProgress";
import { showError, showSuccess } from "@/lib/toast";
const C = {
  bg: "rgba(10,11,14,0.82)",
  card: "rgba(15,17,23,0.75)",
  card2: "rgba(22,26,34,0.72)",
  border: "rgba(255,255,255,0.07)",
  text: "#e2e8f0",
  muted: "#94a3b8",
  dim: "#64748b",
  primary: "#22c55e",
  green: "#22c55e",
  red: "#ef4444",
  amber: "#f59e0b",
};

/** 托管月化：基础月化% × (1 + 加速万分比/10000)，展示取整 */
function calcCustodyMonthlyRateDisplay(
  baseDailyYieldBps: number,
  aiManageAddSpeedBp = 0
) {
  const basePercent = Math.round((baseDailyYieldBps * 30) / 100);
  const speedPercent = Math.round(aiManageAddSpeedBp / 100);
  const effectivePercent = Math.round(
    basePercent * (1 + aiManageAddSpeedBp / 10000)
  );
  return {
    basePercent,
    speedPercent,
    effectivePercent,
    monthlyRate: `${effectivePercent}%`,
    monthlyRateDetailKey:
      aiManageAddSpeedBp > 0
        ? { base: basePercent, speed: speedPercent }
        : undefined,
  };
}

type CustodyUiOrder = {
  id: string;
  /** 解除子记录 ID，列表 key 用（进行中无） */
  exitRecordId?: string;
  amount: number;
  depositTime: string;
  startEarningTime: string;
  redeemTime: string | null;
  redeemAvailableAt: string | null;
  status: "ongoing" | "completed";
  daysEarned: number;
  totalEarned: number;
  todayEarned: number;
  pendingReward: number;
  rewardSplit: {
    pendingUsdt: number;
    pendingAif: number;
    totalUsdt: number;
    totalAif: number;
    todayUsdt: number;
    todayAif: number;
  };
  /** 有效月化率展示，如 `33%`（含智能体加速） */
  monthlyRate: string;
  /** 有加速时展示，如 `基础30%·加速+10%` */
  monthlyRateDetailKey?: { base: number; speed: number };
  monthProgressPct: number;
  monthJoinedDays: number;
  monthEligibleDays: number;
};

function InfoTip({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Info
          size={11}
          className="inline-block ml-1 cursor-help shrink-0"
          style={{ color: C.dim }}
        />
      </TooltipTrigger>
      <TooltipContent
        className="max-w-xs text-xs"
        style={{
          background: C.card2,
          border: `1px solid ${C.border}`,
          color: C.text,
        }}
      >
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

// ===== 用户须知弹窗 =====
function DisclaimerDialog({
  open,
  onClose,
  onAgree,
  mode = "deposit",
  minDeposit,
  maxPerUser,
  baseDailyYieldBps,
  claimFeeBps,
  reinvestMinPendingStatic,
}: {
  open: boolean;
  onClose: () => void;
  onAgree: () => void;
  mode?: "deposit" | "reinvest";
  minDeposit: number;
  maxPerUser: number;
  baseDailyYieldBps: number;
  claimFeeBps: number;
  reinvestMinPendingStatic: number;
}) {
  const { t } = useTranslation();
  const [agreed, setAgreed] = useState(false);
  const isReinvest = mode === "reinvest";
  const monthlyBaseRatePercentText = ((baseDailyYieldBps * 30) / 100).toFixed(
    2
  );
  const claimFeePercentText = (claimFeeBps / 100).toFixed(2);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-sm max-h-[80vh] overflow-y-auto"
        style={{
          background: "#0d0f14",
          border: `1px solid ${isReinvest ? "rgba(245,158,11,0.25)" : "rgba(34,197,94,0.20)"}`,
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle
            className="flex items-center gap-2"
            style={{ color: isReinvest ? C.amber : C.primary }}
          >
            <FileText size={16} />
            {isReinvest
              ? t("custodyPage.disclaimerTitleReinvest")
              : t("custodyPage.disclaimerTitleDeposit")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-xs" style={{ color: C.muted }}>
          <p className="font-semibold" style={{ color: C.text }}>
            {isReinvest
              ? t("custodyPage.disclaimerAgreementReinvest")
              : t("custodyPage.disclaimerAgreementDeposit")}
          </p>
          {isReinvest && (
            <div
              className="rounded-lg p-3"
              style={{
                background: "rgba(245,158,11,0.06)",
                border: "1px solid rgba(245,158,11,0.15)",
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                <Repeat size={12} style={{ color: C.amber }} />
                <span
                  className="text-xs font-semibold"
                  style={{ color: C.amber }}
                >
                  {t("custodyPage.reinvestNoFeeTitle")}
                </span>
              </div>
              <p className="text-[11px]" style={{ color: C.muted }}>
                {t("custodyPage.reinvestNoFeeDesc")}
              </p>
            </div>
          )}
          <div
            className="space-y-2 rounded-lg p-3"
            style={{ background: C.card2, border: `1px solid ${C.border}` }}
          >
            {isReinvest ? (
              <>
                <p>
                  1.{" "}
                  {t("custodyPage.reinvestRule1", {
                    min: formatUSDT(reinvestMinPendingStatic),
                  })}
                </p>
                <p>2. {t("custodyPage.reinvestRule2")}</p>
                <p>3. {t("custodyPage.reinvestRule3")}</p>
                <p>4. {t("custodyPage.reinvestRule4")}</p>
                <p>5. {t("custodyPage.reinvestRule5")}</p>
              </>
            ) : (
              <>
                <p>
                  1.{" "}
                  {t("custodyPage.depositRule1", {
                    min: formatUSDT(minDeposit),
                    max: formatUSDT(maxPerUser),
                  })}
                </p>
                <p>2. {t("custodyPage.depositRule2")}</p>
                <p>
                  3.{" "}
                  {t("custodyPage.depositRule3", {
                    rate: monthlyBaseRatePercentText,
                  })}
                </p>
                <p>4. {t("custodyPage.depositRule4")}</p>
                <p>5. {t("custodyPage.depositRule5")}</p>
                <p>
                  6.{" "}
                  {t("custodyPage.depositRule6", {
                    fee: claimFeePercentText,
                  })}
                </p>
                <p>
                  7.{" "}
                  {t("custodyPage.depositRule7", {
                    min: formatUSDT(reinvestMinPendingStatic),
                  })}
                </p>
                <p>8. {t("custodyPage.depositRule8")}</p>
                <p>9. {t("custodyPage.depositRule9")}</p>
                <p>10. {t("custodyPage.depositRule10")}</p>
                <p>11. {t("custodyPage.depositRule11")}</p>
              </>
            )}
          </div>
          <label className="flex items-start gap-2 cursor-pointer pt-2">
            <input
              type="checkbox"
              checked={agreed}
              onChange={e => setAgreed(e.target.checked)}
              className="mt-0.5 accent-green-500"
            />
            <span className="text-xs" style={{ color: C.text }}>
              {t("custodyPage.agreeTerms")}
            </span>
          </label>
        </div>
        <div className="flex gap-2 mt-2">
          <Button
            variant="outline"
            className="flex-1 text-xs"
            onClick={onClose}
            style={{
              borderColor: C.border,
              color: C.muted,
              background: "transparent",
            }}
          >
            {t("teamPage.claimCancel")}
          </Button>
          <button
            className={`flex-1 py-2.5 rounded-lg font-bold text-sm ${isReinvest ? "" : "btn-primary"}`}
            disabled={!agreed}
            style={{
              opacity: agreed ? 1 : 0.4,
              ...(isReinvest
                ? {
                    background: "rgba(245,158,11,0.15)",
                    border: "1px solid rgba(245,158,11,0.35)",
                    color: C.amber,
                  }
                : {}),
            }}
            onClick={() => {
              onAgree();
              onClose();
            }}
          >
            {isReinvest
              ? t("custodyPage.agreeAndReinvest")
              : t("custodyPage.agreeAndContinue")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===== 存入弹窗 =====
function DepositDialog({
  open,
  onClose,
  minDeposit,
  minDepositUsdtWei,
  maxPerUser,
  maxDepositUsdt,
  maxDepositUsdtWei,
  baseDailyYieldBps,
  claimFeeBps,
  agreementVersion,
}: {
  open: boolean;
  onClose: () => void;
  minDeposit: number;
  minDepositUsdtWei: string;
  /** 单用户入金托管上限（展示） */
  maxPerUser: number;
  /** 本次可存入剩余额度（展示） */
  maxDepositUsdt: number;
  /** 个人剩余可存额度（wei，接口口径相减） */
  maxDepositUsdtWei: string;
  baseDailyYieldBps: number;
  claimFeeBps: number;
  agreementVersion?: string;
}) {
  const { t } = useTranslation();
  const { getTokenBalanceData } = useBalanceV2();
  const custodyDeposit = useCustodyDeposit();
  const availableWei = BigInt(
    getTokenBalanceData(USDT_ADDRESS)?.canUseUsdtAmount ?? "0"
  );
  const remainingWei = BigInt(maxDepositUsdtWei ?? "0");
  const maxDepositWei =
    remainingWei < availableWei ? remainingWei : availableWei;
  const maxDepositDisplay = formatEtherWithDecimals(maxDepositWei, 2);
  const maxDepositAmount = Number(maxDepositDisplay);
  const availableUsdt = Number(formatEtherWithDecimals(availableWei, 2));
  const minDepositWei = BigInt(minDepositUsdtWei ?? "0");
  const [amount, setAmount] = useState("");
  const [useMaxDepositWei, setUseMaxDepositWei] = useState(false);
  useEffect(() => {
    if (!open) return;
    setUseMaxDepositWei(false);
    setAmount(maxDepositWei > 0n ? maxDepositDisplay : "");
  }, [open, maxDepositWei, maxDepositDisplay]);
  const [step, setStep] = useState<"form" | "pending" | "success">("form");
  const isSubmitting = custodyDeposit.isPending;
  const amountNum = +amount || 0;
  const dailyRate = baseDailyYieldBps / 10000;
  const monthlyRate = dailyRate * 30;
  const monthlyRatePercentText = `${monthlyRate * 100}%`;
  const dailyEarning = amountNum * dailyRate * 0.45; // user gets 45%
  const monthlyEarning = amountNum * monthlyRate * 0.45;
  const handleAmountChange = (value: string) => {
    if (value && !/^\d*\.?\d*$/.test(value)) return;
    const [formatted, isGtMax] = formatNumberInput(value, maxDepositAmount);
    setAmount(formatted);
    setUseMaxDepositWei(formatted === maxDepositDisplay);
    if (isGtMax) {
      showError(t("custodyPage.amountExceedsLimit"));
    }
  };
  const setQuickAmount = (pct: number) => {
    if (pct >= 1) {
      setAmount(maxDepositDisplay);
      setUseMaxDepositWei(true);
      return;
    }
    setUseMaxDepositWei(false);
    const portionWei =
      (maxDepositWei * BigInt(Math.round(pct * 10_000))) / 10_000n;
    setAmount(formatEtherWithDecimals(portionWei, 2));
  };
  const handleClose = () => {
    setStep("form");
    setAmount("");
    setUseMaxDepositWei(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-sm"
        style={{
          background: "#0d0f14",
          border: "1px solid rgba(34,197,94,0.20)",
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: C.primary }}>
            {t("custodyPage.depositTitle")}
          </DialogTitle>
        </DialogHeader>
        <AnimatePresence mode="wait">
          {step === "form" && (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              <div
                className="rounded-lg p-3 flex items-center justify-between"
                style={{
                  background: "rgba(34,197,94,0.06)",
                  border: "1px solid rgba(34,197,94,0.18)",
                }}
              >
                <div>
                  <div
                    className="text-[10px] uppercase tracking-wider mb-0.5"
                    style={{ color: C.dim }}
                  >
                    {t("custodyPage.monthlyYield")}
                  </div>
                  <div
                    className="mono-num font-bold text-2xl"
                    style={{ color: C.primary }}
                  >
                    {monthlyRatePercentText}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] mb-0.5" style={{ color: C.dim }}>
                    {t("custodyPage.v1v5Boost")}
                  </div>
                  <div
                    className="mono-num font-bold text-lg"
                    style={{ color: C.amber }}
                  >
                    +10%
                  </div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-2">
                  <span style={{ color: C.muted }}>
                    {t("custodyPage.depositAmount")} (USDT)
                    <InfoTip
                      text={t("custodyPage.depositAmountHint", {
                        min: formatUSDT(minDeposit),
                        max: formatUSDT(maxDepositUsdt),
                        userMax: formatUSDT(maxPerUser),
                      })}
                    />
                  </span>
                  <span className="mono-num" style={{ color: C.muted }}>
                    {t("custodyPage.available")}: ${formatUSDT(availableUsdt)}
                  </span>
                </div>
                <div className="flex gap-2 mb-2">
                  {[
                    { label: "25%", pct: 0.25 },
                    { label: "50%", pct: 0.5 },
                    { label: "75%", pct: 0.75 },
                    { label: t("custodyPage.all"), pct: 1 },
                  ].map(item => {
                    const val = (maxDepositAmount * item.pct).toFixed(2);
                    return (
                      <button
                        key={item.label}
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => setQuickAmount(item.pct)}
                        className="flex-1 py-1.5 rounded text-xs font-semibold"
                        style={{
                          background:
                            Number(amount) === Number(val)
                              ? "rgba(34,197,94,0.10)"
                              : C.card2,
                          border: `1px solid ${amount === val ? "rgba(34,197,94,0.35)" : C.border}`,
                          color:
                            Number(amount) === Number(val)
                              ? C.primary
                              : C.muted,
                          opacity: isSubmitting ? 0.6 : 1,
                        }}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                </div>
                <input
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={e => handleAmountChange(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg text-sm mono-num outline-none"
                  style={{
                    background: C.card2,
                    border: `1px solid ${C.border}`,
                    color: C.text,
                  }}
                  disabled={isSubmitting}
                  min={minDeposit}
                  max={maxDepositAmount}
                />
                <div
                  className="flex justify-between text-[10px] mt-1.5"
                  style={{ color: C.dim }}
                >
                  <span>
                    {t("custodyPage.min")} {formatUSDT(minDeposit)} USDT
                  </span>
                  <span>
                    {t("custodyPage.max")} {formatUSDT(maxDepositUsdt)} USDT
                  </span>
                </div>
              </div>
              <div
                className="rounded-lg p-3 space-y-2 text-xs"
                style={{ background: C.card2, border: `1px solid ${C.border}` }}
              >
                <div className="font-semibold mb-1" style={{ color: C.muted }}>
                  {t("custodyPage.estimatedEarnings")}（
                  {t("custodyPage.userTakeHome")} 45%）
                </div>
                <div className="flex justify-between">
                  <span style={{ color: C.dim }}>
                    {t("custodyPage.dailyEarnings")}
                  </span>
                  <span
                    className="mono-num font-semibold"
                    style={{ color: C.green }}
                  >
                    ~${formatUSDT(dailyEarning)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: C.dim }}>
                    {t("custodyPage.monthlyEarningsEst")}
                  </span>
                  <span
                    className="mono-num font-semibold"
                    style={{ color: C.amber }}
                  >
                    ~${formatUSDT(monthlyEarning)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: C.dim }}>
                    {t("custodyPage.earningsComposition")}
                  </span>
                  <span className="mono-num" style={{ color: C.muted }}>
                    {t("custodyPage.earningsCompositionValue")}
                  </span>
                </div>
              </div>

              {/* 计息规则 */}
              <div
                className="rounded-lg p-3 text-[10px] space-y-1.5"
                style={{
                  background: "rgba(34,197,94,0.04)",
                  border: "1px solid rgba(34,197,94,0.12)",
                }}
              >
                {[
                  t("custodyPage.ruleEarningStart"),
                  t("custodyPage.ruleMergeOrders"),
                  t("custodyPage.ruleClaimFee", {
                    fee: (claimFeeBps / 100).toFixed(2),
                  }),
                  t("custodyPage.ruleAgentBoost"),
                  t("custodyPage.ruleExitPenalty"),
                ].map((rule, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-1.5"
                    style={{ color: C.muted }}
                  >
                    <div
                      className="w-1 h-1 rounded-full mt-1 shrink-0"
                      style={{ background: C.primary }}
                    />
                    {rule}
                  </div>
                ))}
              </div>

              <button
                className="w-full py-3 rounded-lg font-bold text-sm btn-primary disabled:opacity-50"
                onClick={async () => {
                  if (!agreementVersion) {
                    showError(t("custodyPage.signAgreementFirst"));
                    return;
                  }
                  let amountUsdtWei = "";
                  let inputWei = 0n;
                  try {
                    const isMaxAmount =
                      useMaxDepositWei || amount.trim() === maxDepositDisplay;
                    amountUsdtWei = isMaxAmount
                      ? maxDepositWei.toString()
                      : BigInt(parseEther(amount.trim())).toString();
                    inputWei = BigInt(amountUsdtWei);
                  } catch {
                    showError(t("custodyPage.invalidAmount"));
                    return;
                  }
                  if (inputWei <= 0n) {
                    showError(t("custodyPage.invalidAmount"));
                    return;
                  }
                  if (inputWei < minDepositWei) {
                    showError(
                      t("custodyPage.minDepositError", {
                        min: formatUSDT(minDeposit),
                      })
                    );
                    return;
                  }
                  if (inputWei > remainingWei) {
                    showError(
                      t("custodyPage.maxDepositError", {
                        max: formatUSDT(maxDepositUsdt),
                      })
                    );
                    return;
                  }
                  if (inputWei > availableWei) {
                    showError(t("custodyPage.insufficientBalance"));
                    return;
                  }
                  try {
                    setStep("pending");
                    const depositRes = await custodyDeposit.mutateAsync({
                      amountUsdtWei,
                      agreementVersion,
                    });
                    if (
                      depositRes.resultCode === CustodyDepositResultCode.Success
                    ) {
                      setStep("success");
                      return;
                    }
                    setStep("form");
                  } catch {
                    showError(t("custodyPage.depositFailedRetry"));
                    setStep("form");
                  }
                }}
                disabled={isSubmitting || amountNum === 0}
              >
                {t("custodyPage.confirmDeposit")} ${amountNum}
              </button>
            </motion.div>
          )}
          {step === "pending" && (
            <motion.div
              key="pending"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center py-8 gap-4"
            >
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{
                  background: "rgba(34,197,94,0.08)",
                  border: "1px solid rgba(34,197,94,0.25)",
                }}
              >
                <RefreshCw
                  size={24}
                  className="animate-spin"
                  style={{ color: C.primary }}
                />
              </div>
              <p className="text-sm" style={{ color: C.muted }}>
                {t("custodyPage.waitingChainConfirm")}
              </p>
            </motion.div>
          )}
          {step === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center py-6 gap-4"
            >
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{
                  background: "rgba(34,197,94,0.08)",
                  border: "1px solid rgba(34,197,94,0.25)",
                }}
              >
                <CheckCircle size={28} style={{ color: C.green }} />
              </div>
              <div className="text-center">
                <p className="font-semibold mb-1">
                  {t("custodyPage.depositSuccess")}
                </p>
                <p className="text-sm" style={{ color: C.muted }}>
                  {t("custodyPage.earningStartHint")}
                </p>
              </div>
              <button
                className="w-full py-3 rounded-lg font-bold text-sm btn-primary"
                onClick={handleClose}
              >
                {t("teamPage.claimDone")}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

// ===== 赎回弹窗 =====
function RedeemDialog({
  open,
  onClose,
  order,
}: {
  open: boolean;
  onClose: () => void;
  order: CustodyUiOrder | null;
}) {
  const { t } = useTranslation();
  const custodyExit = useCustodyExit();
  const [step, setStep] = useState<"confirm" | "pending" | "success">(
    "confirm"
  );
  if (!order) return null;
  const handleClose = () => {
    setStep("confirm");
    onClose();
  };

  // Calculate exit penalties
  const days = order.daysEarned;
  let timePenaltyRate = 0;
  if (days < 30) timePenaltyRate = 0.05;
  else if (days < 90) timePenaltyRate = 0.03;
  const timePenalty = order.amount * timePenaltyRate;
  const netReturn = order.amount - timePenalty;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-sm"
        style={{
          background: "#0d0f14",
          border: "1px solid rgba(239,68,68,0.20)",
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: C.amber }}>
            {t("custodyPage.redeemTitle")}
          </DialogTitle>
        </DialogHeader>
        <AnimatePresence mode="wait">
          {step === "confirm" && (
            <motion.div
              key="confirm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-3"
            >
              <div
                className="rounded-lg p-3 space-y-2 text-xs"
                style={{ background: C.card2, border: `1px solid ${C.border}` }}
              >
                <div className="flex justify-between">
                  <span style={{ color: C.muted }}>
                    {t("custodyPage.principal")}
                  </span>
                  <span className="mono-num font-medium">
                    ${formatUSDT(order.amount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: C.muted }}>
                    {t("custodyPage.profitEarned")}
                  </span>
                  <span className="mono-num font-medium">
                    ${formatUSDT(order.totalEarned)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: C.muted }}>
                    {t("custodyPage.custodyDays")}
                  </span>
                  <span className="mono-num font-medium">
                    {days} {t("custodyPage.days")}
                  </span>
                </div>
                <div className="h-px" style={{ background: C.border }} />
                <div className="flex justify-between">
                  <span style={{ color: C.red }}>
                    {t("custodyPage.timePenalty")} (
                    {(timePenaltyRate * 100).toFixed(0)}%
                    {t("custodyPage.principalShort")})
                  </span>
                  <span
                    className="mono-num font-medium"
                    style={{ color: C.red }}
                  >
                    -${formatUSDT(timePenalty)}
                  </span>
                </div>
                <div className="h-px" style={{ background: C.border }} />
                <div className="flex justify-between font-semibold">
                  <span style={{ color: C.text }}>
                    {t("custodyPage.estimatedArrival")}
                  </span>
                  <span className="mono-num" style={{ color: C.green }}>
                    ${formatUSDT(Math.max(netReturn, 0))}
                  </span>
                </div>
              </div>

              {/* Warning */}
              <div
                className="rounded-lg p-3 text-[10px] flex items-start gap-2"
                style={{
                  background: "rgba(239,68,68,0.06)",
                  color: C.red,
                  border: "1px solid rgba(239,68,68,0.15)",
                }}
              >
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <span>{t("custodyPage.redeemCooldownHint")}</span>
                </div>
              </div>

              {/* Time penalty table */}
              <div
                className="rounded-lg p-3 text-[10px]"
                style={{ background: C.card2, border: `1px solid ${C.border}` }}
              >
                <div
                  className="font-semibold text-xs mb-2"
                  style={{ color: C.muted }}
                >
                  {t("custodyPage.timePenaltyRules")}
                </div>
                <div className="space-y-1">
                  {[
                    {
                      range: t("custodyPage.penaltyRangeLt30"),
                      rate: "5%",
                      active: days < 30,
                    },
                    {
                      range: t("custodyPage.penaltyRange30to90"),
                      rate: "3%",
                      active: days >= 30 && days < 90,
                    },
                    {
                      range: t("custodyPage.penaltyRangeGte90"),
                      rate: "0%",
                      active: days >= 90,
                    },
                  ].map(row => (
                    <div
                      key={row.range}
                      className="flex justify-between py-1 px-2 rounded"
                      style={{
                        background: row.active
                          ? "rgba(34,197,94,0.08)"
                          : "transparent",
                        border: row.active
                          ? "1px solid rgba(34,197,94,0.20)"
                          : "1px solid transparent",
                      }}
                    >
                      <span style={{ color: row.active ? C.primary : C.dim }}>
                        {row.range}
                      </span>
                      <span
                        className="mono-num"
                        style={{ color: row.active ? C.primary : C.dim }}
                      >
                        {t("custodyPage.deductPrincipal")} {row.rate}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 text-xs"
                  onClick={handleClose}
                  style={{
                    borderColor: C.border,
                    color: C.muted,
                    background: "transparent",
                  }}
                >
                  {t("teamPage.claimCancel")}
                </Button>
                <button
                  className="flex-1 py-2.5 rounded-lg font-bold text-sm"
                  style={{ background: C.red, color: "white" }}
                  disabled={custodyExit.isPending}
                  onClick={async () => {
                    if (!order.id) {
                      showError(t("custodyPage.invalidOrderId"));
                      return;
                    }
                    setStep("pending");
                    try {
                      const res = await custodyExit.mutateAsync({
                        mergedOrderId: order.id,
                      });
                      if (
                        res.resultCode === CustodyExitResultCode.Success &&
                        res.ok
                      ) {
                        setStep("success");
                        return;
                      }
                      showError(res.resultMsg || t("custodyPage.redeemFailed"));
                      setStep("confirm");
                    } catch {
                      showError(t("custodyPage.redeemFailedRetry"));
                      setStep("confirm");
                    }
                  }}
                >
                  {t("custodyPage.confirmRedeem")}
                </button>
              </div>
            </motion.div>
          )}
          {step === "pending" && (
            <motion.div
              key="pending"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center py-8 gap-3"
            >
              <RefreshCw
                size={28}
                className="animate-spin"
                style={{ color: C.amber }}
              />
              <p className="text-sm" style={{ color: C.muted }}>
                {t("custodyPage.processing")}
              </p>
            </motion.div>
          )}
          {step === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center py-6 gap-3"
            >
              <CheckCircle size={36} style={{ color: C.green }} />
              <p className="font-semibold">
                {t("custodyPage.redeemSubmitted")}
              </p>
              <p className="text-xs text-center" style={{ color: C.muted }}>
                {t("custodyPage.redeemCooldownUnlock")}
              </p>
              <button
                className="w-full py-2.5 rounded-lg font-bold text-sm btn-primary"
                onClick={handleClose}
              >
                {t("teamPage.claimDone")}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

// ===== 领取收益弹窗 =====
function ClaimDialog({
  open,
  onClose,
  order,
}: {
  open: boolean;
  onClose: () => void;
  order: CustodyUiOrder | null;
}) {
  const { t } = useTranslation();
  const custodyClaim = useCustodyClaim();
  const tokenNameTsk = getTokenName(CCC_AIF_ADDRESS);
  const tskLocked = !CUSTODY_TSK_TOKEN_LAUNCHED;
  const [step, setStep] = useState<"confirm" | "pending" | "success">(
    "confirm"
  );
  if (!order) return null;
  const handleClose = () => {
    setStep("confirm");
    onClose();
  };
  const pendingUsdt = order.rewardSplit.pendingUsdt;
  const pendingAif = order.rewardSplit.pendingAif;
  const netUsdt = pendingUsdt;
  const netAif = pendingAif;
  const pendingTskUsd = pendingAif;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-sm"
        style={{
          background: "#0d0f14",
          border: "1px solid rgba(34,197,94,0.20)",
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: C.primary }}>
            {t("custodyPage.claimEarnings")}
          </DialogTitle>
        </DialogHeader>
        <AnimatePresence mode="wait">
          {step === "confirm" && (
            <motion.div
              key="confirm"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              <div
                className="rounded-lg p-4 space-y-3"
                style={{ background: C.card, border: `1px solid ${C.border}` }}
              >
                <div
                  className="text-xs font-semibold mb-1"
                  style={{ color: C.muted }}
                >
                  {t("custodyPage.claimableThisTime")}
                </div>
                {/* USDT */}
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{
                    background: "rgba(34,197,94,0.06)",
                    border: "1px solid rgba(34,197,94,0.18)",
                  }}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center"
                      style={{ background: "rgba(34,197,94,0.12)" }}
                    >
                      <span
                        className="text-xs font-bold"
                        style={{ color: C.green }}
                      >
                        $
                      </span>
                    </div>
                    <span className="text-sm font-semibold">USDT</span>
                  </div>
                  <span
                    className="mono-num font-bold text-base"
                    style={{ color: C.green }}
                  >
                    +${formatUSDT(netUsdt)}
                  </span>
                </div>
                {/* TSK - 未发行：置灰，按 USDT 折合价值展示 */}
                <div
                  className="relative flex items-center justify-between overflow-hidden rounded-lg p-3"
                  style={
                    tskLocked
                      ? custodyTskLockedCardStyle
                      : {
                          background: "rgba(34,197,94,0.06)",
                          border: "1px solid rgba(34,197,94,0.18)",
                        }
                  }
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center"
                      style={{ background: "rgba(34,197,94,0.12)" }}
                    >
                      <span
                        className="text-xs font-bold"
                        style={{ color: tskLocked ? C.dim : C.primary }}
                      >
                        T
                      </span>
                    </div>
                    <span
                      className="text-sm font-semibold"
                      style={{ color: tskLocked ? C.dim : C.text }}
                    >
                      {tokenNameTsk}
                    </span>
                  </div>
                  <div className="text-right">
                    <div
                      className="mono-num text-base font-bold"
                      style={{ color: tskLocked ? C.dim : C.primary }}
                    >
                      {formatCustodyTskUsdValue(pendingTskUsd)}
                    </div>
                  </div>
                </div>
                {tskLocked && (
                  <div
                    className="flex items-start gap-1.5 rounded-md px-2 py-1.5 text-[10px]"
                    style={{
                      background: "rgba(245,158,11,0.06)",
                      border: "1px solid rgba(245,158,11,0.15)",
                      color: C.amber,
                    }}
                  >
                    <Lock size={10} className="mt-0.5 shrink-0" />
                    <span>
                      {t("custodyPage.tskNotLaunchedHint", { tokenNameTsk })}
                    </span>
                  </div>
                )}
              </div>
              <div
                className="rounded-lg p-3 text-[10px] flex items-start gap-1.5"
                style={{
                  background: "rgba(34,197,94,0.04)",
                  border: "1px solid rgba(34,197,94,0.12)",
                  color: C.muted,
                }}
              >
                <Gift
                  size={11}
                  className="mt-0.5 shrink-0"
                  style={{ color: C.primary }}
                />
                <span>
                  {tskLocked
                    ? t("custodyPage.claimFeeHintUsdtOnly")
                    : t("custodyPage.claimWalletHint")}
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 text-xs"
                  onClick={handleClose}
                  style={{
                    borderColor: C.border,
                    color: C.muted,
                    background: "transparent",
                  }}
                >
                  {t("teamPage.claimCancel")}
                </Button>
                <button
                  className="flex-1 py-2.5 rounded-lg font-bold text-sm btn-primary"
                  disabled={custodyClaim.isPending}
                  onClick={async () => {
                    if (!order.id) {
                      showError(t("custodyPage.invalidOrderId"));
                      return;
                    }
                    setStep("pending");
                    try {
                      const res = await custodyClaim.mutateAsync({
                        mergedOrderId: order.id,
                      });
                      if (res.ok) {
                        setStep("success");
                        return;
                      }
                      showError(res.resultMsg || t("custodyPage.claimFailed"));
                      setStep("confirm");
                    } catch {
                      showError(t("custodyPage.claimFailedRetry"));
                      setStep("confirm");
                    }
                  }}
                >
                  {t("custodyPage.confirmClaim")}
                </button>
              </div>
            </motion.div>
          )}
          {step === "pending" && (
            <motion.div
              key="pending"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center py-8 gap-4"
            >
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{
                  background: "rgba(34,197,94,0.08)",
                  border: "1px solid rgba(34,197,94,0.25)",
                }}
              >
                <RefreshCw
                  size={24}
                  className="animate-spin"
                  style={{ color: C.primary }}
                />
              </div>
              <p className="text-sm" style={{ color: C.muted }}>
                {t("custodyPage.processing")}
              </p>
            </motion.div>
          )}
          {step === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center py-6 gap-4"
            >
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{
                  background: "rgba(34,197,94,0.08)",
                  border: "1px solid rgba(34,197,94,0.25)",
                }}
              >
                <CheckCircle size={28} style={{ color: C.green }} />
              </div>
              <div className="text-center">
                <p className="font-semibold mb-2">
                  {t("custodyPage.claimSuccess")}
                </p>
                <div className="space-y-1 text-sm">
                  <p>
                    <span style={{ color: C.muted }}>USDT：</span>
                    <span
                      className="mono-num font-bold"
                      style={{ color: C.green }}
                    >
                      +${formatUSDT(netUsdt)}
                    </span>
                  </p>
                  {tskLocked ? (
                    // <p className="text-[11px]" style={{ color: C.dim }}>
                    //   {t("custodyPage.tskClaimPendingHint", {
                    //     tokenNameTsk,
                    //     usdValue: formatCustodyTskUsdValue(netAif),
                    //   })}
                    // </p>
                    ""
                  ) : (
                    <p>
                      <span style={{ color: C.muted }}>{tokenNameTsk}：</span>
                      <span
                        className="mono-num font-bold"
                        style={{ color: C.primary }}
                      >
                        {formatCustodyTskUsdValue(netAif)}
                      </span>
                    </p>
                  )}
                </div>
              </div>
              <button
                className="w-full py-2.5 rounded-lg font-bold text-sm btn-primary"
                onClick={handleClose}
              >
                {t("teamPage.claimDone")}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

/* Animated counter with easeOutBack overshoot */
function CustodyAnimNum({
  value,
  prefix = "",
  suffix = "",
  color,
  refreshKey = 0,
}: {
  value: number;
  prefix?: string;
  suffix?: string;
  color: string;
  refreshKey?: number;
}) {
  const [display, setDisplay] = useState("0");
  const rafRef = useRef<number>(0);
  useEffect(() => {
    const target = value;
    const decimals = value >= 1000 ? 0 : 2;
    const duration = 1200;
    let startTime = 0;
    function animate(now: number) {
      if (!startTime) startTime = now;
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const c1 = 1.70158;
      const c3 = c1 + 1;
      const eased =
        progress === 1
          ? 1
          : 1 + c3 * Math.pow(progress - 1, 3) + c1 * Math.pow(progress - 1, 2);
      const current = eased * target;
      if (target >= 1000)
        setDisplay(
          current.toLocaleString("en-US", { maximumFractionDigits: 0 })
        );
      else setDisplay(current.toFixed(decimals));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    }
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, refreshKey]);
  return (
    <span className="mono-num font-bold" style={{ color }}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}

export default function CustodyTrading() {
  const { t } = useTranslation();
  const { address } = useAccount();
  const { data: custodyConfigData, isLoading: isCustodyConfigLoading } =
    useGetCustodyConfig();
  const signCustodyAgreement = useSignCustodyAgreement();
  const custodyReinvest = useCustodyReinvest();
  const [statsRefreshKey, setStatsRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState<"active" | "history" | "earnings">(
    "active"
  );
  const custodyTabs = ["active", "history", "earnings"] as const;
  const [custodyTabDir, setCustodyTabDir] = useState<1 | -1>(1);
  const handleCustodyTab = (t: typeof activeTab) => {
    const oldIdx = custodyTabs.indexOf(activeTab);
    const newIdx = custodyTabs.indexOf(t);
    setCustodyTabDir(newIdx > oldIdx ? 1 : -1);
    setActiveTab(t);
  };
  const [depositOpen, setDepositOpen] = useState(false);
  const [depositAgreementVersion, setDepositAgreementVersion] = useState("");
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [disclaimerMode, setDisclaimerMode] = useState<"deposit" | "reinvest">(
    "deposit"
  );
  const [redeemOrder, setRedeemOrder] = useState<CustodyUiOrder | null>(null);
  const [claimOrder, setClaimOrder] = useState<CustodyUiOrder | null>(null);
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [expandedRedeemedOrder, setExpandedRedeemedOrder] = useState<
    string | null
  >(null);
  const [earningsSubTab, setEarningsSubTab] = useState<
    "distributed" | "claimed"
  >("distributed");

  const monthlyQuota =
    custodyConfigData?.monthlyQuotaUsdtWei !== undefined
      ? Number(formatEtherWithDecimals(custodyConfigData.monthlyQuotaUsdtWei))
      : 0;
  const usedMonthlyQuota =
    custodyConfigData?.usedMonthlyPrincipalUsdtWei !== undefined
      ? Number(
          formatEtherWithDecimals(custodyConfigData.usedMonthlyPrincipalUsdtWei)
        )
      : 0;
  const minDeposit =
    custodyConfigData?.minOrderUsdtWei !== undefined
      ? Number(formatEtherWithDecimals(custodyConfigData.minOrderUsdtWei))
      : 0;
  const reinvestMinPendingStatic =
    custodyConfigData?.reinvestMinPendingStaticWei !== undefined
      ? Number(
          formatEtherWithDecimals(custodyConfigData.reinvestMinPendingStaticWei)
        )
      : 0;
  const baseDailyYieldBps = custodyConfigData?.baseDailyYieldBps ?? 100;
  const claimFeeBps = custodyConfigData?.claimFeeBps ?? 1000;
  const monthlyBaseRatePercent = (baseDailyYieldBps * 30) / 100;

  const { data: activeCustodyOrdersData } = useGetCustodyOrders();
  const {
    data: exitRecordPages,
    isLoading: isExitRecordsLoading,
    isFetchingNextPage: isExitRecordsFetchingNextPage,
    hasNextPage: hasExitRecordsNextPage,
    fetchNextPage: fetchNextExitRecordsPage,
  } = useGetCustodyExitRecords();
  const exitRecordsLoadMoreRef = useRef<HTMLDivElement | null>(null);
  const { data: userCustodyInfoData } = useGetUserCustodyInfo();
  const {
    data: dailyPayoutPages,
    isLoading: isDailyPayoutLoading,
    isFetchingNextPage: isDailyPayoutFetchingNextPage,
    hasNextPage: hasDailyPayoutNextPage,
    fetchNextPage: fetchNextDailyPayoutPage,
  } = useGetCustodyDailyPayoutRecords();
  const dailyPayoutLoadMoreRef = useRef<HTMLDivElement | null>(null);
  const {
    data: staticClaimPages,
    isLoading: isStaticClaimLoading,
    isFetchingNextPage: isStaticClaimFetchingNextPage,
    hasNextPage: hasStaticClaimNextPage,
    fetchNextPage: fetchNextStaticClaimPage,
  } = useGetCustodyStaticClaimLines();
  const staticClaimLoadMoreRef = useRef<HTMLDivElement | null>(null);

  const formatTs = (ts?: number) =>
    ts && ts > 0 ? new Date(ts).toLocaleString() : "-";
  const mapCustodyOrderToUi = (order: CustodyOrderItem): CustodyUiOrder => {
    const pendingUsdtVal = Number(
      formatEtherWithDecimals(order.pendingStaticUsdtWei)
    );
    const pendingTskValueUsdtVal = Number(
      formatEtherWithDecimals(order.pendingExchangeTskValueUsdtWei)
    );
    const claimedUsdtVal = Number(
      formatEtherWithDecimals(order.claimedStaticUsdtWei)
    );
    const claimedTskValueUsdtVal = Number(
      formatEtherWithDecimals(order.claimedExchangeTskValueUsdtWei)
    );
    const todayUsdt = Number(
      formatEtherWithDecimals(order.todayEarningsUsdtWei ?? "0")
    );
    const todayTskValueUsdt = Number(
      formatEtherWithDecimals(order.todayEarningsTskValueUsdtWei ?? "0")
    );
    const monthProgress = calcCustodyMonthProgress(order.tradeStartsAt);
    const monthlyRateDisplay = calcCustodyMonthlyRateDisplay(
      baseDailyYieldBps,
      order.aiManageAddSpeedBp ?? 0
    );
    return {
      id: order.mergedOrderId,
      amount: Number(formatEtherWithDecimals(order.principalUsdtWei)),
      depositTime: formatTs(order.firstDepositAt),
      startEarningTime: formatTs(order.tradeStartsAt),
      redeemTime: order.status === 2 ? formatTs(order.coolingEndsAt) : null,
      redeemAvailableAt:
        order.status === 2 ? formatTs(order.lockUntilMs) : null,
      status: order.status === 1 ? "ongoing" : "completed",
      daysEarned: calcUtcInclusiveCalendarDays(
        order.tradeStartsAt,
        order.status === 2 ? order.coolingEndsAt : Date.now()
      ),
      totalEarned:
        claimedUsdtVal +
        claimedTskValueUsdtVal +
        pendingUsdtVal +
        pendingTskValueUsdtVal,
      todayEarned: todayUsdt + todayTskValueUsdt,
      pendingReward: pendingUsdtVal + pendingTskValueUsdtVal,
      rewardSplit: {
        pendingUsdt: pendingUsdtVal,
        pendingAif: pendingTskValueUsdtVal,
        totalUsdt: claimedUsdtVal + pendingUsdtVal,
        totalAif: claimedTskValueUsdtVal + pendingTskValueUsdtVal,
        todayUsdt,
        todayAif: todayTskValueUsdt,
      },
      monthlyRate: monthlyRateDisplay.monthlyRate,
      monthlyRateDetailKey: monthlyRateDisplay.monthlyRateDetailKey,
      monthProgressPct: monthProgress.pct,
      monthJoinedDays: monthProgress.joinedDays,
      monthEligibleDays: monthProgress.eligibleDays,
    };
  };

  const mapCustodyExitRecordToUi = (
    record: CustodyExitRecordItem
  ): CustodyUiOrder => {
    const totalUsdt = Number(formatEtherWithDecimals(record.onlyUsdtWei));
    const totalTskUsd = Number(formatEtherWithDecimals(record.tskValueUsdtWei));
    const monthlyRateDisplay = calcCustodyMonthlyRateDisplay(
      baseDailyYieldBps,
      record.aiManageAddSpeedBp ?? 0
    );
    return {
      id: record.mergedOrderId,
      exitRecordId: record.id,
      amount: Number(formatEtherWithDecimals(record.exitedPrincipalUsdtWei)),
      depositTime: formatTs(record.firstDepositAt),
      startEarningTime: formatTs(record.firstDepositAt),
      redeemTime: formatTs(record.exitedAt),
      redeemAvailableAt: formatTs(record.lockUntilMs),
      status: "completed",
      daysEarned: calcUtcInclusiveCalendarDays(
        record.firstDepositAt,
        record.exitedAt
      ),
      totalEarned: totalUsdt + totalTskUsd,
      todayEarned: 0,
      pendingReward: 0,
      rewardSplit: {
        pendingUsdt: 0,
        pendingAif: 0,
        totalUsdt,
        totalAif: totalTskUsd,
        todayUsdt: 0,
        todayAif: 0,
      },
      monthlyRate: monthlyRateDisplay.monthlyRate,
      monthlyRateDetailKey: monthlyRateDisplay.monthlyRateDetailKey,
      monthProgressPct: 0,
      monthJoinedDays: 0,
      monthEligibleDays: 0,
    };
  };

  const activeOrders = useMemo(
    () => (activeCustodyOrdersData?.orders ?? []).map(mapCustodyOrderToUi),
    [activeCustodyOrdersData?.orders, baseDailyYieldBps]
  );
  const completedOrders = useMemo(
    () =>
      exitRecordPages?.pages.flatMap(page =>
        (page.records ?? []).map(mapCustodyExitRecordToUi)
      ) ?? [],
    [exitRecordPages, baseDailyYieldBps]
  );
  const exitRecordsTotal = exitRecordPages?.pages[0]?.total ?? 0;
  const dailyPayoutRecords = useMemo(
    () => dailyPayoutPages?.pages.flatMap(page => page.records ?? []) ?? [],
    [dailyPayoutPages]
  );
  const staticClaimLines = useMemo(
    () => staticClaimPages?.pages.flatMap(page => page.lines ?? []) ?? [],
    [staticClaimPages]
  );
  const formatStatDay = (statDay: string) => {
    if (/^\d{8}$/.test(statDay))
      return `${statDay.slice(4, 6)}-${statDay.slice(6)}`;
    return statDay;
  };
  const formatClaimTime = (ts?: number) => {
    if (!ts || ts <= 0) return "-";
    const d = new Date(ts);
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getMonth() + 1}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  };

  const totalCustody = Number(
    formatEtherWithDecimals(userCustodyInfoData?.totalCustodyUsdtWei ?? "0")
  );
  const todayEarnedUsdt = Number(
    formatEtherWithDecimals(userCustodyInfoData?.todayEarningsUsdtWei ?? "0")
  );
  const todayEarnedTskValueUsdt = Number(
    formatEtherWithDecimals(
      userCustodyInfoData?.todayEarningsTskValueUsdtWei ?? "0"
    )
  );
  const todayEarnedTsk = todayEarnedTskValueUsdt;
  const pendingUsdt = Number(
    formatEtherWithDecimals(userCustodyInfoData?.pendingUsdtWei ?? "0")
  );
  const pendingTsk = Number(
    formatEtherWithDecimals(userCustodyInfoData?.pendingTskValueUsdtWei ?? "0")
  );
  const claimedUsdt = Number(
    formatEtherWithDecimals(userCustodyInfoData?.claimedUsdtWei ?? "0")
  );
  const claimedTsk = Number(
    formatEtherWithDecimals(userCustodyInfoData?.claimedTskWei ?? "0")
  );
  const myCustodyPrincipal = Number(
    formatEtherWithDecimals(
      userCustodyInfoData?.myCustodyPrincipalUsdtWei ?? "0"
    )
  );
  const personalCustodyLimit = Number(
    formatEtherWithDecimals(
      userCustodyInfoData?.myCustodyMaxPerUserUsdtWei ?? "0"
    )
  );
  const hasValidPersonalCustodyLimit = personalCustodyLimit > 0;
  const maxCustodyWei = BigInt(
    userCustodyInfoData?.myCustodyMaxPerUserUsdtWei ?? "0"
  );
  const principalCustodyWei = BigInt(
    userCustodyInfoData?.myCustodyPrincipalUsdtWei ?? "0"
  );
  const depositQuotaRemainingWei = maxCustodyWei - principalCustodyWei;

  const depositQuotaRemaining = Number(
    formatEtherWithDecimals(depositQuotaRemainingWei, 2)
  );
  const userQuotaRatio =
    personalCustodyLimit > 0
      ? Math.min(myCustodyPrincipal / personalCustodyLimit, 1)
      : 0;
  const isUserQuotaReached =
    userCustodyInfoData?.myCustodyExceedsMaxLimit === true ||
    (personalCustodyLimit > 0 && myCustodyPrincipal >= personalCustodyLimit);

  const handleRefresh = async () => {
    await new Promise(r => setTimeout(r, 1200));
    setStatsRefreshKey(k => k + 1);
    showSuccess(t("custodyPage.dataRefreshed"));
  };

  useEffect(() => {
    if (earningsSubTab !== "distributed") return;
    const node = dailyPayoutLoadMoreRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (!hasDailyPayoutNextPage || isDailyPayoutFetchingNextPage) return;
        void fetchNextDailyPayoutPage();
      },
      { threshold: 0.1, rootMargin: "180px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    earningsSubTab,
    hasDailyPayoutNextPage,
    isDailyPayoutFetchingNextPage,
    fetchNextDailyPayoutPage,
  ]);

  useEffect(() => {
    if (activeTab !== "history") return;
    const node = exitRecordsLoadMoreRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (!hasExitRecordsNextPage || isExitRecordsFetchingNextPage) return;
        void fetchNextExitRecordsPage();
      },
      { threshold: 0.1, rootMargin: "180px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    activeTab,
    hasExitRecordsNextPage,
    isExitRecordsFetchingNextPage,
    fetchNextExitRecordsPage,
  ]);

  useEffect(() => {
    if (earningsSubTab !== "claimed") return;
    const node = staticClaimLoadMoreRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (!hasStaticClaimNextPage || isStaticClaimFetchingNextPage) return;
        void fetchNextStaticClaimPage();
      },
      { threshold: 0.1, rootMargin: "180px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    earningsSubTab,
    hasStaticClaimNextPage,
    isStaticClaimFetchingNextPage,
    fetchNextStaticClaimPage,
  ]);

  // 一键复投 handler — 只复投 USDT 部分
  const [reinvestDialogOpen, setReinvestDialogOpen] = useState(false);
  const [reinvestStep, setReinvestStep] = useState<"pending" | "success">(
    "pending"
  );
  const [lastReinvestedUsdt, setLastReinvestedUsdt] = useState(0);
  const handleReinvestClick = () => {
    if (pendingUsdt < reinvestMinPendingStatic) {
      showError(
        t("custodyPage.reinvestInsufficient", {
          min: formatUSDT(reinvestMinPendingStatic),
        })
      );
      return;
    }
    setDisclaimerMode("reinvest");
    setDisclaimerOpen(true);
  };
  const handleReinvestConfirm = async () => {
    if (!address) {
      showError(t("custodyPage.connectWalletFirst"));
      return;
    }
    const agreementVersion = `复投-${address}-${Date.now()}`;
    try {
      const signRes = await signCustodyAgreement.mutateAsync({
        agreementVersion,
      });
      if (!signRes.ok) {
        showError(t("custodyPage.signReinvestFailed"));
        return;
      }
      setLastReinvestedUsdt(pendingUsdt);
      setReinvestStep("pending");
      setReinvestDialogOpen(true);

      const reinvestRes = await custodyReinvest.mutateAsync({
        agreementVersion,
      });
      setLastReinvestedUsdt(
        Number(formatEtherWithDecimals(reinvestRes.reinvestedUsdtWei ?? "0"))
      );
      setReinvestStep("success");
    } catch {
      setReinvestDialogOpen(false);
      showError(t("custodyPage.reinvestFailedRetry"));
    }
  };

  // 领取 TSK handler
  const [claimTskConfirmOpen, setClaimTskConfirmOpen] = useState(false);
  const handleClaimTsk = () => {
    if (pendingTsk <= 0) {
      showError(t("custodyPage.noTskToClaim"));
      return;
    }
    setClaimTskConfirmOpen(true);
  };
  const handleClaimTskConfirm = () => {
    const fee = pendingTsk * 0.1;
    const actual = pendingTsk - fee;
    setClaimTskConfirmOpen(false);
    showSuccess(
      t("custodyPage.claimTskSuccess", {
        actual: formatUSDT(actual),
        fee: formatUSDT(fee),
      })
    );
  };

  // 存入前先签署用户须知
  const [limitAlertOpen, setLimitAlertOpen] = useState(false);
  const [quotaInfoOpen, setQuotaInfoOpen] = useState(false);
  const handleDepositClick = () => {
    setDepositAgreementVersion("");
    if (isUserQuotaReached) {
      setLimitAlertOpen(true);
      return;
    }
    setDisclaimerMode("deposit");
    setDisclaimerOpen(true);
  };
  const handleDepositAgreementAndOpen = async () => {
    if (!address) {
      showError(t("custodyPage.connectWalletFirst"));
      return;
    }
    const agreementVersion = `存入-${address}-${Date.now()}`;
    try {
      const signRes = await signCustodyAgreement.mutateAsync({
        agreementVersion,
      });
      if (!signRes.ok) {
        showError(t("custodyPage.signDepositFailed"));
        return;
      }
      setDepositAgreementVersion(agreementVersion);
      setDepositOpen(true);
    } catch {
      showError(t("custodyPage.signDepositFailedRetry"));
    }
  };
  return (
    // <PullToRefresh onRefresh={handleRefresh}>
    <div
      className="pt-[62px] relative min-w-screen min-h-screen"
      style={{ background: "rgba(10,11,14,0.55)" }}
    >
      {/* Header */}
      <ScrollReveal
        direction="up"
        distance={18}
        duration={0.45}
        className="px-4 pt-5 pb-5"
        style={{
          background: "rgba(13,17,23,0.85)",
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        <h1 className="text-xl font-bold mb-0.5">
          <span style={{ color: C.primary }}>TradingSkills</span>
          <span style={{ color: C.text }}> {t("custodyPage.aiCustody")}</span>
        </h1>
        <p className="text-xs mb-4" style={{ color: C.muted }}>
          {t("custodyPage.headerSubtitle", {
            rate: monthlyBaseRatePercent,
          })}
        </p>
        {/* === Stats Dashboard === */}
        <div
          className="rounded-2xl overflow-hidden"
          style={{
            background: "rgba(12,14,20,0.9)",
            backdropFilter: "blur(12px)",
            border: `1px solid ${C.border}`,
          }}
        >
          {/* 今日收益 - 紧凑行 */}
          <div
            className="flex items-center justify-between px-4 py-2.5"
            style={{ borderBottom: `1px solid ${C.border}` }}
          >
            <div
              className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest"
              style={{ color: C.dim }}
            >
              <Gift size={11} />
              {t("custodyPage.todayEarnings")}
            </div>
            <div className="flex items-baseline gap-2">
              <span
                className="mono-num text-base font-bold"
                style={{ color: C.amber }}
              >
                +{formatUSDT(todayEarnedUsdt)}{" "}
                <span className="text-[10px] font-medium opacity-60">USDT</span>
              </span>
              <span className="mono-num text-[11px]" style={{ color: C.green }}>
                + TSK =${formatUSDT(todayEarnedTsk)}
              </span>
            </div>
          </div>

          {/* 待领取 USDT + 待领取 TSK */}
          <div className="grid grid-cols-2">
            <div
              className="px-4 py-2.5"
              style={{ borderRight: `1px solid ${C.border}` }}
            >
              <div
                className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest mb-1"
                style={{ color: C.dim }}
              >
                <Repeat size={10} />
                {t("custodyPage.pending")} USDT
              </div>
              <div
                className="mono-num text-base font-bold leading-none mb-1.5"
                style={{ color: C.amber }}
              >
                {formatUSDT(pendingUsdt)}{" "}
                <span className="text-[10px] font-medium opacity-60">USDT</span>
              </div>
              <div className="flex items-center justify-between gap-1">
                <span className="text-[8px]" style={{ color: C.dim }}>
                  ≥{formatUSDT(reinvestMinPendingStatic)}{" "}
                  {t("custodyPage.canReinvest")}
                </span>
                <button
                  className={`shrink-0 px-2 py-1 rounded-md text-[9px] font-semibold flex items-center gap-1 transition-all ${pendingUsdt >= reinvestMinPendingStatic ? "reinvest-pulse" : ""}`}
                  style={{
                    background:
                      pendingUsdt >= reinvestMinPendingStatic
                        ? "rgba(245,158,11,0.12)"
                        : "rgba(255,255,255,0.03)",
                    border: `1px solid ${pendingUsdt >= reinvestMinPendingStatic ? "rgba(245,158,11,0.35)" : C.border}`,
                    color:
                      pendingUsdt >= reinvestMinPendingStatic ? C.amber : C.dim,
                    opacity: pendingUsdt >= reinvestMinPendingStatic ? 1 : 0.5,
                    cursor:
                      pendingUsdt >= reinvestMinPendingStatic
                        ? "pointer"
                        : "not-allowed",
                  }}
                  onClick={handleReinvestClick}
                  disabled={
                    pendingUsdt < reinvestMinPendingStatic ||
                    pendingUsdt === 0 ||
                    isCustodyConfigLoading
                  }
                >
                  <Repeat size={9} />
                  {t("custodyPage.reinvest")}
                </button>
              </div>
            </div>
            <div className="px-4 py-2.5">
              <div
                className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest mb-1"
                style={{ color: C.dim }}
              >
                <Gift size={10} />
                {t("custodyPage.pending")} TSK
              </div>
              <div
                className="mono-num text-base font-bold leading-none"
                style={{ color: C.green }}
              >
                =${formatUSDT(pendingTsk)}
              </div>
            </div>
          </div>
        </div>

        {/* 托管额度进度条 */}
        <div
          className="mt-2 rounded-xl px-4 py-2.5"
          style={{
            background: "rgba(12,14,20,0.9)",
            backdropFilter: "blur(12px)",
            border: `1px solid ${C.border}`,
          }}
        >
          <div className="flex justify-between items-center text-[10px] mb-1.5">
            <div className="flex items-center gap-1.5">
              <span style={{ color: C.dim }}>
                {t("custodyPage.myCustodyQuota")}
              </span>
              <button
                type="button"
                onClick={() => setQuotaInfoOpen(true)}
                className="flex items-center"
              >
                <AlertCircle size={11} style={{ color: C.amber }} />
              </button>
            </div>
            <span
              className="mono-num"
              style={{
                color: isUserQuotaReached ? "#ef4444" : C.muted,
              }}
            >
              {formatUSDT(myCustodyPrincipal)} /{" "}
              {hasValidPersonalCustodyLimit
                ? formatUSDT(personalCustodyLimit)
                : "--"}{" "}
              USDT
            </span>
          </div>
          <div
            className="h-1.5 rounded-full overflow-hidden"
            style={{ background: "rgba(255,255,255,0.06)" }}
          >
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${userQuotaRatio * 100}%`,
                background: isUserQuotaReached
                  ? "linear-gradient(90deg, #ef4444, #dc2626)"
                  : "linear-gradient(90deg, #22c55e, #3b82f6)",
              }}
            />
          </div>
          {isUserQuotaReached && (
            <p className="text-[9px] mt-1" style={{ color: "#ef4444" }}>
              {t("custodyPage.personalLimitReached")}
            </p>
          )}
        </div>
      </ScrollReveal>

      {/* Action Buttons */}
      <ScrollReveal
        direction="up"
        distance={14}
        delay={0.08}
        duration={0.4}
        className="px-4 pt-4 space-y-3"
      >
        {/* Buttons Row */}
        <div className="flex gap-2">
          <button
            className="flex-1 py-3.5 rounded-lg font-bold text-sm flex items-center justify-center gap-2 btn-primary"
            onClick={handleDepositClick}
          >
            <ArrowDownLeft size={16} />
            {t("custodyPage.depositCustody")}
          </button>
        </div>
      </ScrollReveal>

      {/* Tabs with sliding indicator */}
      <div
        className="relative flex items-center mx-4 mt-4 p-1 rounded-xl"
        style={{ background: C.card, border: `1px solid ${C.border}` }}
      >
        <motion.div
          className="absolute top-1 bottom-1 rounded-lg tab-glow-indicator"
          style={{
            width: `calc(${100 / 3}% - 6px)`,
            background:
              "linear-gradient(180deg, rgba(34,197,94,0.14) 0%, rgba(34,197,94,0.06) 100%)",
            border: "1px solid rgba(34,197,94,0.25)",
            backdropFilter: "blur(8px)",
          }}
          animate={{
            x: `calc(${custodyTabs.indexOf(activeTab)} * (100% + 3px) + 3px)`,
          }}
          transition={{ type: "spring", stiffness: 380, damping: 32 }}
        />
        {[
          {
            id: "active",
            label: t("custodyPage.tabActive", { count: activeOrders.length }),
          },
          {
            id: "history",
            label: t("custodyPage.tabExited", {
              count: exitRecordsTotal || completedOrders.length,
            }),
          },
          { id: "earnings", label: t("custodyPage.earningsHistory") },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => handleCustodyTab(tab.id as typeof activeTab)}
            className="relative z-10 flex-1 py-2.5 text-xs font-semibold text-center transition-colors duration-200"
            style={{ color: activeTab === tab.id ? C.primary : C.dim }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <ScrollReveal
        direction="up"
        distance={20}
        delay={0.12}
        duration={0.45}
        className="px-4 py-4 space-y-4 pb-6"
      >
        <AnimatePresence mode="wait">
          {/* Active Orders */}
          {activeTab === "active" && (
            <motion.div
              key="active"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
              className="space-y-4"
            >
              {activeOrders.length === 0 ? (
                <div
                  className="rounded-lg p-8 text-center"
                  style={{
                    background: C.card,
                    border: "1px dashed rgba(255,255,255,0.12)",
                  }}
                >
                  <Shield
                    size={32}
                    className="mx-auto mb-3"
                    style={{ color: C.dim }}
                  />
                  <p className="text-sm" style={{ color: C.muted }}>
                    {t("custodyPage.noActiveOrders")}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activeOrders.map((order, i) => {
                    const isExpanded = expandedOrder === order.id;
                    return (
                      <motion.div
                        key={order.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.06 }}
                        className="rounded-lg overflow-hidden"
                        style={{
                          background: C.card,
                          border: "1px solid rgba(34,197,94,0.15)",
                        }}
                      >
                        <div
                          className="h-px"
                          style={{
                            background:
                              "linear-gradient(90deg, transparent, rgba(34,197,94,0.40), transparent)",
                          }}
                        />
                        <div className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-8 h-8 rounded-lg flex items-center justify-center"
                                style={{
                                  background: "rgba(34,197,94,0.08)",
                                  border: "1px solid rgba(34,197,94,0.20)",
                                }}
                              >
                                <Shield
                                  size={14}
                                  style={{ color: C.primary }}
                                />
                              </div>
                              <div>
                                <div className="font-semibold text-sm mono-num">
                                  #{order.id.slice(-3)}
                                </div>
                                <div
                                  className="text-[10px]"
                                  style={{ color: C.muted }}
                                >
                                  {t("custodyPage.daysAccrued")}{" "}
                                  {order.daysEarned} {t("custodyPage.days")}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div
                                className="mono-num font-bold text-base"
                                style={{ color: C.green }}
                              >
                                {/* <TickingNumber
                                    baseValue={formatUSDT(order.todayEarned)}
                                    prefix="+$"
                                    suffix=""
                                    minInterval={2000}
                                    maxInterval={5000}
                                    incrementRange={[0.01, 0.05]}
                                  /> */}
                                {formatUSDT(order.todayEarned)}
                              </div>
                              <div
                                className="text-[10px]"
                                style={{ color: C.dim }}
                              >
                                {t("custodyPage.todayEarnings")}
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <div
                              className="rounded p-2"
                              style={{ background: "rgba(255,255,255,0.02)" }}
                            >
                              <div
                                className="text-[10px] mb-0.5"
                                style={{ color: C.dim }}
                              >
                                {t("custodyPage.principal")}
                              </div>
                              <div
                                className="mono-num font-bold text-xs"
                                style={{ color: C.text }}
                              >
                                ${formatUSDT(order.amount)}
                              </div>
                            </div>
                            <div
                              className="rounded p-2"
                              style={{ background: "rgba(255,255,255,0.02)" }}
                            >
                              <div
                                className="text-[10px] mb-0.5"
                                style={{ color: C.dim }}
                              >
                                {t("custodyPage.monthlyRate")}
                              </div>
                              <div
                                className="mono-num font-bold text-xs"
                                style={{ color: C.primary }}
                              >
                                {order.monthlyRate}
                              </div>
                              {order.monthlyRateDetailKey ? (
                                <div
                                  className="text-[9px] mt-0.5 leading-tight"
                                  style={{ color: C.dim }}
                                >
                                  {t(
                                    "custodyPage.monthlyRateDetail",
                                    order.monthlyRateDetailKey
                                  )}
                                </div>
                              ) : null}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mb-3">
                            <div
                              className="rounded p-2"
                              style={{
                                background: "rgba(245,158,11,0.04)",
                                border: "1px solid rgba(245,158,11,0.10)",
                              }}
                            >
                              <div
                                className="text-[10px] mb-0.5"
                                style={{ color: C.dim }}
                              >
                                USDT {t("custodyPage.earnings")}
                              </div>
                              <div
                                className="mono-num font-bold text-xs"
                                style={{ color: C.amber }}
                              >
                                {formatUSDT(order.rewardSplit.totalUsdt)} USDT
                              </div>
                            </div>
                            <div
                              className="rounded p-2"
                              style={{
                                background: "rgba(34,197,94,0.04)",
                                border: "1px solid rgba(34,197,94,0.10)",
                              }}
                            >
                              <div
                                className="text-[10px] mb-0.5"
                                style={{ color: C.dim }}
                              >
                                TSK {t("custodyPage.earnings")}
                              </div>
                              <div
                                className="mono-num font-bold text-xs"
                                style={{ color: C.green }}
                              >
                                ${formatUSDT(order.rewardSplit.totalAif)}
                              </div>
                            </div>
                          </div>
                          <div className="mb-3">
                            <div className="flex justify-between text-[10px] mb-1">
                              <span style={{ color: C.dim }}>
                                {t("custodyPage.monthProgress")}
                              </span>
                              <span
                                className="mono-num"
                                style={{ color: C.muted }}
                              >
                                {/* {order.monthJoinedDays}/
                                  {order.monthEligibleDays} (
                                  {order.monthProgressPct.toFixed(1)}%) */}
                                {order.monthProgressPct.toFixed(1)}%
                              </span>
                            </div>
                            <div
                              className="h-1.5 rounded-full overflow-hidden"
                              style={{ background: "rgba(255,255,255,0.06)" }}
                            >
                              <motion.div
                                className="h-full rounded-full"
                                initial={{ width: 0 }}
                                animate={{
                                  width: `${Math.min(order.monthProgressPct, 100)}%`,
                                }}
                                transition={{ duration: 1, ease: "easeOut" }}
                                style={{ background: C.primary }}
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              className="flex-1 py-2 rounded-lg text-xs font-semibold"
                              style={{
                                background: C.card2,
                                border: `1px solid ${C.border}`,
                                color: C.muted,
                              }}
                              onClick={() =>
                                setExpandedOrder(isExpanded ? null : order.id)
                              }
                            >
                              <span className="flex items-center justify-center gap-1">
                                {isExpanded ? (
                                  <ChevronUp size={11} />
                                ) : (
                                  <ChevronDown size={11} />
                                )}
                                {t("custodyPage.details")}
                              </span>
                            </button>
                            <button
                              className="flex-1 py-2 rounded-lg text-xs font-bold"
                              style={{
                                background: "rgba(34,197,94,0.10)",
                                border: "1px solid rgba(34,197,94,0.25)",
                                color: C.primary,
                              }}
                              onClick={() => setClaimOrder(order)}
                            >
                              {t("custodyPage.claimEarnings")}
                            </button>
                            <button
                              className="flex-1 py-2 rounded-lg text-xs font-bold"
                              style={{
                                background: "rgba(239,68,68,0.08)",
                                border: "1px solid rgba(239,68,68,0.20)",
                                color: C.red,
                              }}
                              onClick={() => setRedeemOrder(order)}
                            >
                              {t("custodyPage.exitCustody")}
                            </button>
                          </div>
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                key="detail"
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                              >
                                <div
                                  className="mt-3 pt-3 space-y-2 text-xs"
                                  style={{
                                    borderTop: `1px solid ${C.border}`,
                                  }}
                                >
                                  <div
                                    className="font-semibold text-xs mb-2"
                                    style={{ color: C.muted }}
                                  >
                                    {t("custodyPage.earningsDetail")}
                                  </div>
                                  {[
                                    [
                                      t("custodyPage.depositTime"),
                                      order.depositTime,
                                    ],
                                    [
                                      t("custodyPage.startAccrual"),
                                      order.startEarningTime,
                                    ],
                                    [
                                      t("custodyPage.pendingUsdtShort"),
                                      `${formatUSDT(order.rewardSplit.pendingUsdt)} USDT`,
                                    ],
                                    [
                                      t("custodyPage.pendingTskShort"),
                                      `$${formatUSDT(order.rewardSplit.pendingAif)} TSK`,
                                    ],
                                    [
                                      t("custodyPage.todayUsdt"),
                                      `+${formatUSDT(order.rewardSplit.todayUsdt)} USDT`,
                                    ],
                                    [
                                      t("custodyPage.todayTsk"),
                                      `+$${formatUSDT(order.rewardSplit.todayAif)} TSK`,
                                    ],
                                  ].map(([k, v]) => (
                                    <div
                                      key={k as string}
                                      className="flex justify-between"
                                    >
                                      <span style={{ color: C.dim }}>{k}</span>
                                      <span
                                        className="mono-num"
                                        style={{ color: C.muted }}
                                      >
                                        {v}
                                      </span>
                                    </div>
                                  ))}
                                  <button
                                    className="w-full mt-2 py-2 rounded-lg text-xs font-bold"
                                    style={{
                                      background: "rgba(34,197,94,0.10)",
                                      border: "1px solid rgba(34,197,94,0.25)",
                                      color: C.primary,
                                    }}
                                    onClick={() => setClaimOrder(order)}
                                  >
                                    {t("custodyPage.claimPendingRewards")}
                                  </button>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}

          {/* Order History */}
          {activeTab === "history" && (
            <motion.div
              key="history"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
              className="space-y-3"
            >
              {completedOrders.length === 0 && !isExitRecordsLoading ? (
                <div className="text-center py-12" style={{ color: C.muted }}>
                  <BarChart2 size={32} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm">{t("custodyPage.noExitedOrders")}</p>
                </div>
              ) : (
                <>
                  {completedOrders.map((order, i) => {
                    const isRedeemedExpanded =
                      expandedRedeemedOrder ===
                      (order.exitRecordId ?? order.id);
                    return (
                      <motion.div
                        key={order.exitRecordId ?? order.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.06 }}
                        className="rounded-lg p-4"
                        style={{
                          background: C.card,
                          border: `1px solid ${C.border}`,
                        }}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-8 h-8 rounded-lg flex items-center justify-center"
                              style={{
                                background: "rgba(255,255,255,0.04)",
                                border: `1px solid ${C.border}`,
                              }}
                            >
                              <Shield size={14} style={{ color: C.dim }} />
                            </div>
                            <div>
                              <div className="font-semibold text-sm mono-num">
                                #{order.id.slice(-3)}
                              </div>
                              <div
                                className="text-[10px]"
                                style={{ color: C.dim }}
                              >
                                {t("custodyPage.exitedTotalDays")}{" "}
                                {order.daysEarned} {t("custodyPage.days")}
                              </div>
                            </div>
                          </div>
                          <span
                            className="px-2 py-0.5 rounded text-[10px] font-semibold"
                            style={{
                              background: "rgba(255,255,255,0.04)",
                              color: C.dim,
                              border: `1px solid ${C.border}`,
                            }}
                          >
                            {t("custodyPage.exited")}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            {
                              label: t("custodyPage.principal"),
                              value: `$${formatUSDT(order.amount)}`,
                              color: C.text,
                            },
                            {
                              label: t("custodyPage.totalEarnings"),
                              value: `$${formatUSDT(order.totalEarned)}`,
                              color: C.green,
                            },
                            {
                              label: t("custodyPage.monthlyRate"),
                              value: order.monthlyRate,
                              detail: order.monthlyRateDetailKey,
                              color: C.amber,
                            },
                          ].map(s => (
                            <div
                              key={s.label}
                              className="rounded p-2"
                              style={{ background: "rgba(255,255,255,0.02)" }}
                            >
                              <div
                                className="text-[10px] mb-0.5"
                                style={{ color: C.dim }}
                              >
                                {s.label}
                              </div>
                              <div
                                className="mono-num font-bold text-xs"
                                style={{ color: s.color }}
                              >
                                {s.value}
                              </div>
                              {s.detail ? (
                                <div
                                  className="text-[9px] mt-0.5 leading-tight"
                                  style={{ color: C.dim }}
                                >
                                  {t("custodyPage.monthlyRateDetail", s.detail)}
                                </div>
                              ) : null}
                            </div>
                          ))}
                        </div>
                        {/* Collapsible details toggle */}
                        <button
                          onClick={() =>
                            setExpandedRedeemedOrder(
                              isRedeemedExpanded
                                ? null
                                : (order.exitRecordId ?? order.id)
                            )
                          }
                          className="w-full flex items-center justify-center gap-1.5 py-2 mt-3 rounded-md text-xs font-medium transition-all duration-200"
                          style={{
                            background: "rgba(255,255,255,0.03)",
                            color: C.dim,
                            border: `1px solid ${C.border}`,
                          }}
                        >
                          <ChevronDown
                            size={14}
                            className={`transition-transform duration-200 ${isRedeemedExpanded ? "rotate-180" : ""}`}
                          />
                          {isRedeemedExpanded
                            ? t("custodyPage.collapseDetails")
                            : t("custodyPage.expandDetails")}
                        </button>
                        {/* Collapsible details content */}
                        <AnimatePresence>
                          {isRedeemedExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div
                                className="space-y-1.5 text-xs pt-3 mt-3"
                                style={{ borderTop: `1px solid ${C.border}` }}
                              >
                                {[
                                  [
                                    t("custodyPage.depositTime"),
                                    order.depositTime,
                                  ],
                                  [
                                    t("custodyPage.redeemTime"),
                                    order.redeemTime || "-",
                                  ],
                                  [
                                    t("custodyPage.usdtEarnings"),
                                    `${formatUSDT(order.rewardSplit.totalUsdt)} USDT`,
                                  ],
                                  [
                                    t("custodyPage.tskEarnings"),
                                    `$${formatUSDT(order.rewardSplit.totalAif)} TSK`,
                                  ],
                                ].map(([k, v]) => (
                                  <div
                                    key={k as string}
                                    className="flex justify-between"
                                  >
                                    <span style={{ color: C.dim }}>{k}</span>
                                    <span
                                      className="mono-num"
                                      style={{ color: C.muted }}
                                    >
                                      {v}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                  {(isExitRecordsLoading || isExitRecordsFetchingNextPage) && (
                    <div className="py-3 flex items-center justify-center gap-2">
                      <RefreshCw
                        size={12}
                        className="animate-spin"
                        style={{ color: C.dim }}
                      />
                      <span className="text-[11px]" style={{ color: C.dim }}>
                        {t("custodyPage.loading")}
                      </span>
                    </div>
                  )}
                  <div ref={exitRecordsLoadMoreRef} className="h-1" />
                </>
              )}
            </motion.div>
          )}

          {/* Earnings History */}
          {activeTab === "earnings" && (
            <motion.div
              key="earnings"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
              className="space-y-3"
            >
              {/* 已发放 / 已领取 统计值 */}
              <div
                className="grid grid-cols-2 rounded-xl overflow-hidden"
                style={{
                  background: "rgba(12,14,20,0.9)",
                  border: `1px solid ${C.border}`,
                }}
              >
                <div
                  className="p-4"
                  style={{ borderRight: `1px solid ${C.border}` }}
                >
                  <div
                    className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest mb-2"
                    style={{ color: C.dim }}
                  >
                    <CircleCheckBig size={11} style={{ color: C.green }} />
                    {t("custodyPage.claimed")} USDT
                  </div>
                  <div
                    className="mono-num text-lg font-bold leading-none"
                    style={{ color: C.amber }}
                  >
                    {formatUSDT(claimedUsdt)}
                  </div>
                </div>
                <div className="p-4">
                  <div
                    className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest mb-2"
                    style={{ color: C.dim }}
                  >
                    <CircleCheckBig size={11} style={{ color: C.green }} />
                    {t("custodyPage.claimed")} TSK
                  </div>
                  <div
                    className="mono-num text-lg font-bold leading-none"
                    style={{ color: C.green }}
                  >
                    {formatUSDT(claimedTsk)}{" "}
                    <span className="text-xs font-medium opacity-60">TSK</span>
                  </div>
                </div>
              </div>

              {/* Sub-tabs: 已发放 / 已领取 */}
              <div
                className="flex rounded-lg p-0.5"
                style={{
                  background: C.card,
                  border: `1px solid ${C.border}`,
                }}
              >
                {[
                  {
                    id: "distributed" as const,
                    label: t("custodyPage.distributed"),
                  },
                  { id: "claimed" as const, label: t("custodyPage.claimed") },
                ].map(sub => (
                  <button
                    key={sub.id}
                    className="flex-1 py-2 text-xs font-semibold rounded-md transition-all duration-200"
                    style={{
                      background:
                        earningsSubTab === sub.id
                          ? "rgba(34,197,94,0.12)"
                          : "transparent",
                      color: earningsSubTab === sub.id ? C.primary : C.dim,
                      border:
                        earningsSubTab === sub.id
                          ? "1px solid rgba(34,197,94,0.25)"
                          : "1px solid transparent",
                    }}
                    onClick={() => setEarningsSubTab(sub.id)}
                  >
                    {sub.label}
                  </button>
                ))}
              </div>

              {/* 已发放 - 每日结算记录 */}
              {earningsSubTab === "distributed" && (
                <>
                  <div
                    className="grid grid-cols-3 text-[10px] py-2 px-3 uppercase tracking-wider"
                    style={{ color: C.dim }}
                  >
                    <span>{t("custodyPage.date")}</span>
                    <span className="text-right">
                      {t("custodyPage.usdtEarnings")}
                    </span>
                    <span className="text-right">
                      {t("custodyPage.tskEarningsUsd")}
                    </span>
                  </div>
                  <div style={{ borderTop: `1px solid ${C.border}` }} />
                  {dailyPayoutRecords.length === 0 && !isDailyPayoutLoading ? (
                    <div
                      className="rounded-lg p-8 text-center mt-3"
                      style={{
                        background: C.card,
                        border: "1px dashed rgba(255,255,255,0.12)",
                      }}
                    >
                      <FileText
                        size={28}
                        className="mx-auto mb-2"
                        style={{ color: C.dim }}
                      />
                      <p className="text-xs" style={{ color: C.muted }}>
                        {t("custodyPage.noDistributionRecords")}
                      </p>
                    </div>
                  ) : (
                    dailyPayoutRecords.map((item, i) => {
                      const usdt = Number(
                        formatEtherWithDecimals(item.onlyUsdt ?? "0")
                      );
                      const tskUsd = Number(
                        formatEtherWithDecimals(item.tskValueUsdt ?? "0")
                      );
                      return (
                        <motion.div
                          key={`${item.id}-${item.time}`}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.02 }}
                          className="grid grid-cols-3 text-xs py-2.5 px-3 rounded"
                          style={{
                            borderBottom: "1px solid rgba(255,255,255,0.04)",
                          }}
                        >
                          <span style={{ color: C.muted }}>
                            {formatStatDay(item.statDay)}
                          </span>
                          <span
                            className="text-right mono-num"
                            style={{ color: C.green }}
                          >
                            +${formatUSDT(usdt)}
                          </span>
                          <span
                            className="text-right mono-num"
                            style={{ color: C.primary }}
                          >
                            +${formatUSDT(tskUsd)}
                          </span>
                        </motion.div>
                      );
                    })
                  )}
                  {(isDailyPayoutLoading || isDailyPayoutFetchingNextPage) && (
                    <div className="py-3 flex items-center justify-center gap-2">
                      <RefreshCw
                        size={12}
                        className="animate-spin"
                        style={{ color: C.dim }}
                      />
                      <span className="text-[11px]" style={{ color: C.dim }}>
                        {t("custodyPage.loading")}
                      </span>
                    </div>
                  )}
                  <div ref={dailyPayoutLoadMoreRef} className="h-1" />
                </>
              )}

              {/* 已领取 - 用户复投/领取记录 */}
              {earningsSubTab === "claimed" && (
                <>
                  {staticClaimLines.length === 0 && !isStaticClaimLoading ? (
                    <div
                      className="rounded-lg p-8 text-center"
                      style={{
                        background: C.card,
                        border: "1px dashed rgba(255,255,255,0.12)",
                      }}
                    >
                      <FileText
                        size={28}
                        className="mx-auto mb-2"
                        style={{ color: C.dim }}
                      />
                      <p className="text-xs" style={{ color: C.muted }}>
                        {t("custodyPage.noClaimRecords")}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div
                        className="grid grid-cols-[1fr_auto_1fr_1fr] text-[10px] py-2 px-3 uppercase tracking-wider gap-2"
                        style={{ color: C.dim }}
                      >
                        <span>{t("custodyPage.time")}</span>
                        <span className="text-center">
                          {t("custodyPage.type")}
                        </span>
                        <span className="text-right">USDT</span>
                        <span className="text-right">TSK</span>
                      </div>
                      <div style={{ borderTop: `1px solid ${C.border}` }} />
                      {staticClaimLines.map((item, i) => {
                        const isReinvest = item.kind === 2;
                        const usdtAmount = formatEtherWithDecimals(
                          item.netPendingStaticUsdtWei ?? "0",
                          2
                        );
                        const tskUsdAmount = formatEtherWithDecimals(
                          item.netPendingTskValueUsdtWei ?? "0"
                        );
                        const tskAmount = formatEtherWithDecimals(
                          item.netTskWei ?? "0"
                        );
                        const tskFeeAmount = formatEtherWithDecimals(
                          item.feeTskWei ?? "0"
                        );
                        return (
                          <motion.div
                            key={item.id || `${item.createTime}-${i}`}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.02 }}
                            className="grid grid-cols-[1fr_auto_1fr_1fr] text-xs py-2.5 px-3 rounded items-center gap-2"
                            style={{
                              borderBottom: "1px solid rgba(255,255,255,0.04)",
                            }}
                          >
                            <span
                              className="text-[10px]"
                              style={{ color: C.muted }}
                            >
                              {formatClaimTime(item.createTime)}
                            </span>
                            <span className="text-center">
                              {isReinvest ? (
                                <span
                                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold"
                                  style={{
                                    background: "rgba(245,158,11,0.12)",
                                    color: C.amber,
                                  }}
                                >
                                  <Repeat size={9} />
                                  {t("custodyPage.reinvest")}
                                </span>
                              ) : (
                                <span
                                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold"
                                  style={{
                                    background: "rgba(34,197,94,0.12)",
                                    color: C.green,
                                  }}
                                >
                                  <ArrowUpRight size={9} />
                                  {t("custodyPage.claimAction")}
                                </span>
                              )}
                            </span>
                            <span
                              className="text-right mono-num font-semibold"
                              style={{
                                color: isReinvest ? C.amber : C.green,
                              }}
                            >
                              {isReinvest
                                ? formatEtherWithDecimals(
                                    item.reinvestPrincipalUsdtWei ?? "0",
                                    2
                                  )
                                : usdtAmount}
                            </span>
                            <span className="text-right">
                              {/* {+tskAmount > 0 ? (
                                  <span className="flex flex-col items-end">
                                    <span
                                      className="mono-num font-semibold"
                                      style={{ color: C.green }}
                                    >
                                      {formatKMBTruncate(tskAmount)}
                                    </span>
                                    {+tskFeeAmount > 0 && (
                                      <span
                                        className="inline-flex items-center gap-0.5 text-[9px]"
                                        style={{ color: C.dim }}
                                      >
                                        <Flame
                                          size={8}
                                          style={{ color: C.red }}
                                        />
                                        {formatKMBTruncate(tskFeeAmount)}{" "}
                                        {t("custodyPage.fee")}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span style={{ color: C.dim }}>-</span>
                                )} */}
                              {+tskUsdAmount > 0 ? (
                                <span
                                  style={{
                                    color: C.green,
                                  }}
                                >
                                  +${tskUsdAmount}
                                </span>
                              ) : (
                                <span style={{ color: C.dim }}>-</span>
                              )}
                            </span>
                          </motion.div>
                        );
                      })}
                    </>
                  )}
                  {(isStaticClaimLoading || isStaticClaimFetchingNextPage) && (
                    <div className="py-3 flex items-center justify-center gap-2">
                      <RefreshCw
                        size={12}
                        className="animate-spin"
                        style={{ color: C.dim }}
                      />
                      <span className="text-[11px]" style={{ color: C.dim }}>
                        {t("custodyPage.loading")}
                      </span>
                    </div>
                  )}
                  <div ref={staticClaimLoadMoreRef} className="h-1" />
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </ScrollReveal>

      <DisclaimerDialog
        open={disclaimerOpen}
        onClose={() => setDisclaimerOpen(false)}
        mode={disclaimerMode}
        minDeposit={minDeposit}
        maxPerUser={personalCustodyLimit}
        baseDailyYieldBps={baseDailyYieldBps}
        claimFeeBps={claimFeeBps}
        reinvestMinPendingStatic={reinvestMinPendingStatic}
        onAgree={() => {
          if (disclaimerMode === "deposit") {
            void handleDepositAgreementAndOpen();
          } else handleReinvestConfirm();
        }}
      />
      <DepositDialog
        open={depositOpen}
        onClose={() => {
          setDepositOpen(false);
          setDepositAgreementVersion("");
        }}
        minDeposit={minDeposit}
        minDepositUsdtWei={custodyConfigData?.minOrderUsdtWei ?? "0"}
        maxPerUser={personalCustodyLimit}
        maxDepositUsdt={depositQuotaRemaining}
        maxDepositUsdtWei={depositQuotaRemainingWei.toString()}
        baseDailyYieldBps={baseDailyYieldBps}
        claimFeeBps={claimFeeBps}
        agreementVersion={depositAgreementVersion}
      />

      {/* 复投 loading/success 弹窗 */}
      <Dialog
        open={reinvestDialogOpen}
        onOpenChange={v => {
          if (!v && reinvestStep === "success") setReinvestDialogOpen(false);
        }}
      >
        <DialogContent
          className="max-w-xs"
          style={{
            background: "#0d0f14",
            border: "1px solid rgba(245,158,11,0.25)",
            color: C.text,
          }}
        >
          <AnimatePresence mode="wait">
            {reinvestStep === "pending" && (
              <motion.div
                key="pending"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center py-8 gap-4"
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center"
                  style={{
                    background: "rgba(245,158,11,0.08)",
                    border: "1px solid rgba(245,158,11,0.25)",
                  }}
                >
                  <RefreshCw
                    size={24}
                    className="animate-spin"
                    style={{ color: C.amber }}
                  />
                </div>
                <p className="text-sm font-medium" style={{ color: C.muted }}>
                  {t("custodyPage.reinvesting")}
                </p>
                <p className="text-xs mono-num" style={{ color: C.amber }}>
                  {formatUSDT(lastReinvestedUsdt)} USDT
                </p>
              </motion.div>
            )}
            {reinvestStep === "success" && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center py-6 gap-4"
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center"
                  style={{
                    background: "rgba(245,158,11,0.08)",
                    border: "1px solid rgba(245,158,11,0.25)",
                  }}
                >
                  <CheckCircle size={28} style={{ color: C.amber }} />
                </div>
                <div className="text-center">
                  <p className="font-semibold mb-1" style={{ color: C.amber }}>
                    {t("custodyPage.reinvestSuccess")}
                  </p>
                  <p className="text-sm" style={{ color: C.muted }}>
                    {t("custodyPage.reinvestTransferred")}{" "}
                    <span
                      className="mono-num font-bold"
                      style={{ color: C.amber }}
                    >
                      {formatUSDT(lastReinvestedUsdt)} USDT
                    </span>{" "}
                    {t("custodyPage.reinvestNewOrder")}
                  </p>
                </div>
                <button
                  className="w-full py-3 rounded-lg font-bold text-sm"
                  style={{
                    background: "rgba(245,158,11,0.12)",
                    border: "1px solid rgba(245,158,11,0.35)",
                    color: C.amber,
                  }}
                  onClick={() => setReinvestDialogOpen(false)}
                >
                  {t("teamPage.claimDone")}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>

      <RedeemDialog
        open={!!redeemOrder}
        onClose={() => setRedeemOrder(null)}
        order={redeemOrder}
      />
      <ClaimDialog
        open={!!claimOrder}
        onClose={() => setClaimOrder(null)}
        order={claimOrder}
      />

      {/* 领取 TSK 确认弹窗 - 显示具体金额 */}
      <Dialog open={claimTskConfirmOpen} onOpenChange={setClaimTskConfirmOpen}>
        <DialogContent
          className="max-w-xs"
          style={{
            background: "#0d0f14",
            border: "1px solid rgba(34,197,94,0.20)",
            color: C.text,
          }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: C.green }}>
              {t("custodyPage.claimTskTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div
              className="rounded-lg p-3"
              style={{ background: C.card, border: `1px solid ${C.border}` }}
            >
              <div className="flex justify-between text-xs mb-2">
                <span style={{ color: C.dim }}>
                  {t("custodyPage.claimable")}
                </span>
                <span className="mono-num font-bold" style={{ color: C.green }}>
                  ${formatUSDT(pendingTsk)} TSK
                </span>
              </div>
              <div className="flex justify-between text-xs mb-2">
                <span style={{ color: C.dim }}>
                  {t("custodyPage.claimFee10")} (10%)
                </span>
                <span className="mono-num" style={{ color: C.red }}>
                  -${formatUSDT(pendingTsk * 0.1)} TSK
                </span>
              </div>
              <div
                style={{
                  borderTop: `1px solid ${C.border}`,
                  marginTop: 8,
                  paddingTop: 8,
                }}
                className="flex justify-between text-xs"
              >
                <span className="font-semibold" style={{ color: C.text }}>
                  {t("custodyPage.actualReceived")}
                </span>
                <span className="mono-num font-bold" style={{ color: C.green }}>
                  ${formatUSDT(pendingTsk * 0.9)} TSK
                </span>
              </div>
            </div>
            <p className="text-[10px]" style={{ color: C.dim }}>
              {t("custodyPage.feeBurnedWarning")}
            </p>
            <div className="flex gap-2">
              <button
                className="flex-1 py-2.5 rounded-lg text-xs font-semibold"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: `1px solid ${C.border}`,
                  color: C.muted,
                }}
                onClick={() => setClaimTskConfirmOpen(false)}
              >
                {t("teamPage.claimCancel")}
              </button>
              <button
                className="flex-1 py-2.5 rounded-lg text-xs font-semibold"
                style={{
                  background: "rgba(34,197,94,0.15)",
                  border: "1px solid rgba(34,197,94,0.4)",
                  color: C.green,
                }}
                onClick={handleClaimTskConfirm}
              >
                {t("custodyPage.confirmClaim")}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 本月额度信息弹窗 */}
      <Dialog open={quotaInfoOpen} onOpenChange={setQuotaInfoOpen}>
        <DialogContent
          className="max-w-[290px] w-[calc(100vw-60px)] p-0 border-0 overflow-hidden bg-[#0d0f14]"
          style={{
            background: "#0d0f14",
            border: "1px solid rgba(34,197,94,0.20)",
            borderRadius: 16,
          }}
        >
          <div className="px-4 py-5">
            <div className="flex items-center gap-2 mb-4">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                style={{ background: "rgba(245,158,11,0.12)" }}
              >
                <AlertCircle size={14} style={{ color: C.amber }} />
              </div>
              <h3 className="text-sm font-bold" style={{ color: C.text }}>
                {t("custodyPage.platformMonthlyQuota")}
              </h3>
            </div>
            <div className="space-y-3">
              <div
                className="rounded-lg px-3 py-2"
                style={{ background: "rgba(255,255,255,0.03)" }}
              >
                <div className="text-[10px] mb-0.5" style={{ color: C.dim }}>
                  {t("custodyPage.monthUsed")}
                </div>
                <div
                  className="mono-num text-lg font-bold"
                  style={{ color: C.text }}
                >
                  {formatUSDT(usedMonthlyQuota)}{" "}
                  <span
                    className="text-[10px] font-normal"
                    style={{ color: C.dim }}
                  >
                    USDT
                  </span>
                </div>
              </div>
              <div
                className="rounded-lg px-3 py-2"
                style={{ background: "rgba(255,255,255,0.03)" }}
              >
                <div className="text-[10px] mb-0.5" style={{ color: C.dim }}>
                  {t("custodyPage.totalQuota")}
                </div>
                <div
                  className="mono-num text-lg font-bold"
                  style={{ color: C.muted }}
                >
                  {formatUSDT(monthlyQuota)}{" "}
                  <span
                    className="text-[10px] font-normal"
                    style={{ color: C.dim }}
                  >
                    USDT
                  </span>
                </div>
              </div>
              <div
                className="h-2 rounded-full overflow-hidden"
                style={{ background: "rgba(255,255,255,0.06)" }}
              >
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${
                      monthlyQuota > 0
                        ? Math.min((usedMonthlyQuota / monthlyQuota) * 100, 100)
                        : 0
                    }%`,
                    background: "linear-gradient(90deg, #22c55e, #f59e0b)",
                  }}
                />
              </div>
              <p
                className="text-[11px] leading-relaxed"
                style={{ color: C.dim }}
              >
                {t("custodyPage.quotaInfoDesc")}
              </p>
            </div>
            <button
              type="button"
              className="w-full mt-4 py-2.5 rounded-lg font-semibold text-sm"
              style={{
                background: "rgba(245,158,11,0.12)",
                border: "1px solid rgba(245,158,11,0.3)",
                color: C.amber,
              }}
              onClick={() => setQuotaInfoOpen(false)}
            >
              {t("custodyPage.gotIt")}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 托管额度超限提示 */}
      <Dialog open={limitAlertOpen} onOpenChange={setLimitAlertOpen}>
        <DialogContent
          className="max-w-[340px] p-0 border-0 overflow-hidden bg-[#0d0f14]"
          style={{
            background: "#0d0f14",
            border: "1px solid rgba(239,68,68,0.25)",
            borderRadius: 16,
          }}
        >
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: "rgba(239,68,68,0.12)" }}
              >
                <AlertTriangle size={16} style={{ color: "#ef4444" }} />
              </div>
              <h3 className="text-base font-bold" style={{ color: C.text }}>
                {t("custodyPage.quotaFullTitle")}
              </h3>
            </div>
            <p
              className="text-sm leading-relaxed mb-2"
              style={{ color: C.muted }}
            >
              {t("custodyPage.quotaFullDesc1")}
              <span className="font-bold" style={{ color: "#ef4444" }}>
                {" "}
                {formatUSDT(personalCustodyLimit)} USDT
              </span>
              {t("custodyPage.quotaFullDesc2")}
            </p>
            <p className="text-xs leading-relaxed" style={{ color: C.dim }}>
              {t("custodyPage.quotaFullDesc3")}
            </p>
            <button
              className="w-full mt-5 py-3 rounded-lg font-semibold text-sm"
              style={{
                background: "rgba(239,68,68,0.12)",
                border: "1px solid rgba(239,68,68,0.3)",
                color: "#ef4444",
              }}
              onClick={() => setLimitAlertOpen(false)}
            >
              {t("custodyPage.gotIt")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    // </PullToRefresh>
  );
}
